import React from 'react';
import { useSelector } from 'react-redux';

import { AccessStatus } from '../../../../../../@types/AccessStatus';
import type { User } from '../../../../../../@types/User';
import type { DashboardExactDates, DashboardPersonAssignments } from '../../../../../../store/dashboardSlice';
import type { RootState } from '../../../../../../store/store';
import { FREEMIUM_ROUTES } from '../../../../../../utils/routes';
import type { ModuleName } from '../../../../../../utils/subscriptions';
import { hasModuleAccess } from '../../../../../../utils/subscriptions';
import type { Module } from '../../types';

interface ModuleCardProps {
  module: Module;
  onModuleClick: (module: Module) => void;
  onShowTooltip: (event: React.MouseEvent, module: Module) => void;
  onHideTooltip: () => void;
  personAssignments?: DashboardPersonAssignments;
  exactDates?: DashboardExactDates;
  companyUsers?: User[];
  restrictedToRowKeys?: string[];
  flatStandalone?: boolean;
}

const getModuleNameFromRoute = (route?: string): ModuleName | null => {
  if (!route) {
    return null;
  }
  const [firstSegment] = route.split('/').filter(Boolean);
  if (firstSegment === 'communication') { return 'communicating'; }
  if (firstSegment === 'prospecting') { return 'prospecting'; }
  if (firstSegment === 'monitoring') { return 'monitoring'; }
  return null;
};

const REFERENT_MODULE_PREFIX = 'MODULE ';
const extractReferentOrder = (value?: string): string | null => {
  if (!value || !value.startsWith(REFERENT_MODULE_PREFIX)) {
    return null;
  }
  const match = value.match(/MODULE\s(\d+)/);
  return match ? match[1] : null;
};

const MOIS_COURTS = ['jan.', 'fév.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function ModuleCard({ 
  module, 
  onModuleClick, 
  onShowTooltip, 
  onHideTooltip, 
  personAssignments,
  exactDates,
  companyUsers,
  restrictedToRowKeys,
  flatStandalone = false,
}: ModuleCardProps) {
  const company = useSelector((state: RootState) => state.auth.company);
  const now = new Date();
  const moduleName = getModuleNameFromRoute(module.route);
  const isFreemiumRoute = module.route ? FREEMIUM_ROUTES.includes(module.route as typeof FREEMIUM_ROUTES[number]) : false;
  const requiresPaidAccess = Boolean(module.route && moduleName && !isFreemiumRoute);

  const hasActiveModuleAccess = moduleName ? hasModuleAccess(company, moduleName) : true;

  let accessStatus = AccessStatus.ACTIVE;

  if (requiresPaidAccess) {
    if (hasActiveModuleAccess) {
      accessStatus = AccessStatus.ACTIVE;
    } else if (company?.proAbonoSubscriptionEnd) {
      const subscriptionEnd = new Date(company.proAbonoSubscriptionEnd);
      accessStatus = now > subscriptionEnd ? AccessStatus.EXPIRED : AccessStatus.MISSING;
    } else {
      accessStatus = AccessStatus.MISSING;
    }
  }

  const isUpcoming = false;
  const lacksAccess = accessStatus === AccessStatus.MISSING || accessStatus === AccessStatus.EXPIRED;
  const isRestrictedOut = Boolean(restrictedToRowKeys && module.planningRowKey && !restrictedToRowKeys.includes(module.planningRowKey));
  const shouldDisable = module.disabled || isUpcoming || lacksAccess || isRestrictedOut;

  const isRichCard = Boolean(module.planningRowKey);
  const moduleNumber = extractReferentOrder(module.number);

  const assignedUser = React.useMemo(() => {
    if (!module.planningRowKey || !personAssignments || !companyUsers) { return null; }
    const userId = personAssignments[module.planningRowKey];
    if (!userId) { return null; }
    return companyUsers.find((u) => u.id === userId) ?? null;
  }, [module.planningRowKey, personAssignments, companyUsers]);

  const resolvedDates = React.useMemo(() => {
    if (!module.planningEventType || !module.planningEventKeys || !exactDates) { return []; }
    return module.planningEventKeys.map((eventKey) => {
      const dateKey = `${module.planningEventType}-${eventKey}`;
      const date = exactDates[dateKey];
      if (!date) { return null; }
      return `${date.day} ${MOIS_COURTS[date.month - 1]}`;
    });
  }, [module.planningEventType, module.planningEventKeys, exactDates]);

  if (isRichCard) {
    return (
      <div className={`overflow-hidden rounded-lg shadow-sm transition-all duration-300 ${
        shouldDisable ? 'opacity-60' : 'hover:shadow-md'
      }`}>
        {/* Title Bar */}
        <div className={`flex items-center gap-3 rounded-t-lg px-4 py-2.5 ${
          shouldDisable ? 'bg-gray-400' : 'bg-rgpd-blue'
        }`}>
          {moduleNumber && (
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-white/50 bg-white text-xs font-bold text-rgpd-blue">
              {moduleNumber}
            </div>
          )}
          <div className="min-w-0 flex-1 text-[1rem] leading-snug font-bold text-white">
            {module.title}
          </div>
          <button
            className={`inline-flex items-center gap-1.5 rounded-md px-6 py-3 text-[0.65rem] font-semibold tracking-wide text-white uppercase transition-all duration-200 ${
              shouldDisable
                ? 'cursor-not-allowed bg-gray-500'
                : 'cursor-pointer bg-rgpd-green hover:-translate-y-px hover:bg-rgpd-green-dark'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (!shouldDisable) { onModuleClick(module); }
            }}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75" />
            </svg>
            Accéder au module
          </button>
        </div>

        {/* Body */}
        <div className="bg-white px-4 py-3">
          <p className="mb-2.5 text-[1rem] leading-relaxed text-gray-800">
            {module.shortDesc}
          </p>

          {/* Responsible Row */}
          <div className="mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.78rem] text-gray-800">
            {assignedUser ? (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-semibold text-rgpd-blue"><span role="img" aria-label="person">👤</span> Responsable</span>
                  <span>{assignedUser.firstname} {assignedUser.lastname}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-semibold text-rgpd-blue"><span role="img" aria-label="email">✉️</span> Email</span>
                  <span className="text-gray-700">{assignedUser.email}</span>
                </span>
              </>
            ) : (
              <span className="text-[0.78rem] text-gray-700 italic">Non assigné — à configurer dans le planning</span>
            )}
          </div>

          {/* Dates / Frequency / No-date note */}
          {module.planningFrequencyBadge ? (
            <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[0.78rem] font-semibold text-emerald-800">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              {module.planningFrequencyBadge}
            </div>
          ) : module.planningNoDateNote ? (
            <p className="text-[0.78rem] text-gray-700 italic">
              <span role="img" aria-label="info">ℹ️</span> {module.planningNoDateNote}
            </p>
          ) : module.planningEventKeys && module.planningDateLabels ? (
            <>
              <div className="mb-1.5 text-[0.65rem] font-bold tracking-wide text-gray-700 uppercase">
                <span role="img" aria-label="calendar">📅</span> Dates planifiées
              </div>
              <div className="flex flex-wrap gap-2">
                {module.planningEventKeys.map((_, index) => {
                  const label = module.planningDateLabels?.[index] ?? `Date ${index + 1}`;
                  const dateStr = resolvedDates[index];
                  return (
                    <div key={index} className="flex flex-col gap-0.5">
                      <span className="text-[0.65rem] font-medium text-gray-700">{label}</span>
                      <div className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-[0.78rem] text-gray-900">
                        {dateStr ?? <span className="text-gray-600 italic">—</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  if (flatStandalone) {
    return (
      <div
        className={`flex items-center gap-5 rounded-xl border border-gray-200 bg-white px-6 py-4.5 shadow-sm transition-shadow duration-200 ${
          shouldDisable ? 'opacity-60' : 'hover:shadow-md'
        }`}
      >
        <div className="relative shrink-0">
          <div className={`flex h-[52px] w-[52px] items-center justify-center rounded-full text-[22px] ${
            shouldDisable
              ? 'bg-gray-300'
              : 'bg-gradient-to-br from-rgpd-blue to-violet-600'
          }`}>
            <span>{module.icon}</span>
          </div>
          {moduleNumber && (
            <div className="absolute -right-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-rgpd-blue-dark text-[11px] font-black text-white">
              {moduleNumber}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className={`mb-1 text-[10.5px] font-extrabold tracking-wider uppercase ${
            shouldDisable ? 'text-gray-400' : 'text-rgpd-blue'
          }`}>
            {module.number}
          </div>
          <div className={`mb-1 text-sm font-bold ${
            shouldDisable ? 'text-gray-400' : 'text-gray-900'
          }`}>
            {module.title}
          </div>
          <div className={`text-xs leading-relaxed ${
            shouldDisable ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {module.shortDesc}
          </div>
        </div>

        <button
          type="button"
          className={`shrink-0 rounded-[7px] px-4 py-2 text-[11px] font-bold whitespace-nowrap text-white transition-opacity ${
            shouldDisable
              ? 'cursor-not-allowed bg-gray-400'
              : 'bg-rgpd-blue-dark hover:opacity-85'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (!shouldDisable) { onModuleClick(module); }
          }}
        >
          Accéder →
        </button>
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4 transition-all duration-300 ${
        shouldDisable
          ? 'cursor-not-allowed opacity-60'
          : 'cursor-pointer hover:-translate-y-0.5 hover:border-rgpd-blue hover:shadow-lg'
      }`}
      onClick={() => {
        if (shouldDisable) { return; }
        onModuleClick(module);
      }}
      onMouseEnter={(e) => {
        if (shouldDisable) { return; }
        onShowTooltip(e, module);
      }}
      onMouseMove={(e) => {
        if (shouldDisable) { return; }
        onShowTooltip(e, module);
      }}
      onMouseLeave={onHideTooltip}
    >
      <div className="relative">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-lg transition-all duration-300 ${
          shouldDisable
            ? 'border-gray-300 bg-gray-200 text-gray-400' 
            : 'border-rgpd-blue/20 bg-rgpd-blue-light shadow-md hover:scale-110 hover:shadow-lg'
        }`}>
          <span className={`filter ${shouldDisable ? '' : 'drop-shadow-sm'}`}>{module.icon}</span>
        </div>
        {moduleNumber && (
          <div className="absolute -right-2 -bottom-2 flex h-5 w-5 items-center justify-center rounded-full bg-rgpd-blue text-[0.6rem] font-semibold text-white shadow-md">
            {moduleNumber}
          </div>
        )}
      </div>

      <div className="flex-1">
        <div className="mb-2">
          <div className={`mb-1 text-xs font-semibold ${
            shouldDisable ? 'text-gray-400' : 'text-rgpd-blue'
          }`}>
            {module.number}
          </div>
          <div className={`text-sm leading-tight font-semibold ${
            shouldDisable ? 'text-gray-400' : 'text-gray-800'
          }`}>
            {module.title}
          </div>
        </div>
        <div className={`text-xs leading-relaxed ${
          shouldDisable ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {module.shortDesc}
        </div>
      </div>
    </div>
  );
}

export default ModuleCard;
